// frontend/src/components/mascot/BearSVG.tsx
// TaisaCore Sprint 10: Sentinel Bear SVG Graphics

import React from 'react';
import { animated, SpringValue } from '@react-spring/web';
import { BearState } from '../../types/dashboard';

interface BearSVGProps {
  size?: number;
  state: BearState;
  eyeOffset: { x: number; y: number };
  eyeSpring: {
    transform: SpringValue<string>;
  };
}

export const BearSVG: React.FC<BearSVGProps> = ({
  size = 120,
  state,
  eyeOffset,
  eyeSpring,
}) => {
  // Colors
  const furColor = '#8B7355';
  const furDark = '#6B5344';
  const furLight = '#A89070';
  const snoutColor = '#D4C4A8';
  const noseColor = '#2D2D2D';
  const eyeWhite = '#FFFFFF';
  const eyePupil = '#1A1A1A';
  const cheekColor = '#E8A0A0';
  const scrubsColor = '#0D7377';
  const scrubsLight = '#14A3A8';

  // State-based modifiers
  const isAlert = state === 'alert';
  const isThinking = state === 'thinking';
  const isSuccess = state === 'success';

  return (
    <svg
      width={size}
      height={size * 1.2}
      viewBox="0 0 120 144"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Definitions */}
      <defs>
        <linearGradient id="furGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={furLight} />
          <stop offset="100%" stopColor={furColor} />
        </linearGradient>
        <linearGradient id="scrubsGradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={scrubsLight} />
          <stop offset="100%" stopColor={scrubsColor} />
        </linearGradient>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.15" />
        </filter>
      </defs>

      {/* Body / Scrubs */}
      <ellipse
        cx="60"
        cy="120"
        rx="35"
        ry="24"
        fill="url(#scrubsGradient)"
        filter="url(#shadow)"
      />
      
      {/* Scrubs V-neck */}
      <path
        d="M48 100 L60 115 L72 100"
        fill="none"
        stroke={scrubsLight}
        strokeWidth="2"
      />

      {/* Ears */}
      <circle cx="30" cy="30" r="18" fill={furColor} filter="url(#shadow)" />
      <circle cx="30" cy="30" r="10" fill={furDark} />
      <circle cx="90" cy="30" r="18" fill={furColor} filter="url(#shadow)" />
      <circle cx="90" cy="30" r="10" fill={furDark} />

      {/* Head */}
      <ellipse
        cx="60"
        cy="55"
        rx="42"
        ry="38"
        fill="url(#furGradient)"
        filter="url(#shadow)"
      />

      {/* Snout */}
      <ellipse cx="60" cy="68" rx="18" ry="14" fill={snoutColor} />

      {/* Nose */}
      <ellipse
        cx="60"
        cy="62"
        rx="8"
        ry="6"
        fill={noseColor}
      />
      {/* Nose shine */}
      <ellipse cx="57" cy="60" rx="2" ry="1.5" fill="#4A4A4A" />

      {/* Mouth - changes with state */}
      {isSuccess ? (
        // Big smile for success
        <path
          d="M48 72 Q60 82 72 72"
          fill="none"
          stroke={noseColor}
          strokeWidth="2"
          strokeLinecap="round"
        />
      ) : isAlert ? (
        // Concerned mouth for alert
        <path
          d="M52 75 Q60 72 68 75"
          fill="none"
          stroke={noseColor}
          strokeWidth="2"
          strokeLinecap="round"
        />
      ) : (
        // Normal slight smile
        <path
          d="M52 72 Q60 76 68 72"
          fill="none"
          stroke={noseColor}
          strokeWidth="2"
          strokeLinecap="round"
        />
      )}

      {/* Cheeks */}
      <ellipse cx="35" cy="65" rx="6" ry="4" fill={cheekColor} opacity="0.5" />
      <ellipse cx="85" cy="65" rx="6" ry="4" fill={cheekColor} opacity="0.5" />

      {/* Eyes - with gaze tracking */}
      <g>
        {/* Left eye white */}
        <ellipse cx="45" cy="50" rx="10" ry="11" fill={eyeWhite} />
        {/* Left eye pupil - animated */}
        <animated.g style={eyeSpring}>
          <ellipse
            cx={45 + eyeOffset.x * 0.8}
            cy={50 + eyeOffset.y * 0.8}
            rx={isAlert ? 5 : 4}
            ry={isAlert ? 6 : 5}
            fill={eyePupil}
          />
          {/* Pupil shine */}
          <circle
            cx={43 + eyeOffset.x * 0.8}
            cy={48 + eyeOffset.y * 0.8}
            r="1.5"
            fill="#FFFFFF"
          />
        </animated.g>
        
        {/* Right eye white */}
        <ellipse cx="75" cy="50" rx="10" ry="11" fill={eyeWhite} />
        {/* Right eye pupil - animated */}
        <animated.g style={eyeSpring}>
          <ellipse
            cx={75 + eyeOffset.x * 0.8}
            cy={50 + eyeOffset.y * 0.8}
            rx={isAlert ? 5 : 4}
            ry={isAlert ? 6 : 5}
            fill={eyePupil}
          />
          {/* Pupil shine */}
          <circle
            cx={73 + eyeOffset.x * 0.8}
            cy={48 + eyeOffset.y * 0.8}
            r="1.5"
            fill="#FFFFFF"
          />
        </animated.g>
      </g>

      {/* Eyebrows - change with state */}
      {isAlert && (
        <>
          <path
            d="M36 38 L50 42"
            stroke={furDark}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <path
            d="M84 38 L70 42"
            stroke={furDark}
            strokeWidth="3"
            strokeLinecap="round"
          />
        </>
      )}

      {isThinking && (
        <>
          <path
            d="M38 40 L52 40"
            stroke={furDark}
            strokeWidth="2.5"
            strokeLinecap="round"
          />
          <path
            d="M68 40 L82 40"
            stroke={furDark}
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </>
      )}

      {/* Arms */}
      {/* Left arm */}
      <ellipse
        cx="25"
        cy="110"
        rx="10"
        ry="18"
        fill={furColor}
        transform={isSuccess ? 'rotate(-30 25 110)' : 'rotate(15 25 110)'}
      />
      {/* Right arm */}
      <ellipse
        cx="95"
        cy="110"
        rx="10"
        ry="18"
        fill={furColor}
        transform={isSuccess ? 'rotate(30 95 110)' : 'rotate(-15 95 110)'}
      />

      {/* Paws */}
      <ellipse cx="22" cy="95" rx="8" ry="6" fill={furLight} />
      <ellipse cx="98" cy="95" rx="8" ry="6" fill={furLight} />

      {/* Stethoscope */}
      <path
        d="M45 100 Q40 95 42 88 Q45 82 50 85"
        fill="none"
        stroke="#333333"
        strokeWidth="2"
      />
      <circle cx="42" cy="88" r="4" fill="#666666" stroke="#333333" strokeWidth="1" />

      {/* Thinking indicator */}
      {isThinking && (
        <g className="thinking-dots">
          <circle cx="100" cy="30" r="4" fill="#0D7377" opacity="0.6">
            <animate
              attributeName="opacity"
              values="0.6;1;0.6"
              dur="1s"
              repeatCount="indefinite"
            />
          </circle>
          <circle cx="108" cy="22" r="3" fill="#0D7377" opacity="0.4">
            <animate
              attributeName="opacity"
              values="0.4;0.8;0.4"
              dur="1s"
              repeatCount="indefinite"
              begin="0.2s"
            />
          </circle>
          <circle cx="114" cy="16" r="2" fill="#0D7377" opacity="0.3">
            <animate
              attributeName="opacity"
              values="0.3;0.6;0.3"
              dur="1s"
              repeatCount="indefinite"
              begin="0.4s"
            />
          </circle>
        </g>
      )}

      {/* Success sparkles */}
      {isSuccess && (
        <g className="success-sparkles">
          <path d="M15 20 L18 25 L15 30 L12 25 Z" fill="#FFD700">
            <animate
              attributeName="opacity"
              values="1;0.5;1"
              dur="0.5s"
              repeatCount="indefinite"
            />
          </path>
          <path d="M105 15 L108 20 L105 25 L102 20 Z" fill="#FFD700">
            <animate
              attributeName="opacity"
              values="0.5;1;0.5"
              dur="0.5s"
              repeatCount="indefinite"
            />
          </path>
        </g>
      )}

      {/* Alert warning sign (held in paw when alert) */}
      {isAlert && (
        <g transform="translate(95, 70) rotate(15)">
          {/* Sign pole */}
          <rect x="-2" y="0" width="4" height="30" fill="#8B4513" rx="1" />
          {/* Warning triangle */}
          <path
            d="M0 -20 L15 10 L-15 10 Z"
            fill="#FFD700"
            stroke="#FF6B00"
            strokeWidth="2"
          />
          {/* Exclamation mark */}
          <text
            x="0"
            y="5"
            textAnchor="middle"
            fontSize="18"
            fontWeight="bold"
            fill="#FF6B00"
          >
            !
          </text>
        </g>
      )}
    </svg>
  );
};

export default BearSVG;
